import { Injectable, Component, Directive, HostBinding } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { HostListener, ElementRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { languageService } from './language';
import { viewService } from './view';
import { AppConstants } from '@unpispas/upp-defs';

export type KeyboardLayoutRow = { [key: string]: string };
export type KeyboardLayout = Array<KeyboardLayoutRow>;

/**********************************/
/* SERVICE TO STORE KEYBOARD      */
/**********************************/

/**
 * @service kioskService
 * 
 * This service is responsible for managing a virtual keyboard in an Angular application. 
 * It provides methods to show and hide the keyboard, simulate key presses, 
 * and interact with an associated input element. It also emits events when 
 * the keyboard is shown or hidden, allowing components to react accordingly.
 * 
 * @description
 * The `kioskService` is designed to be injected at the root level of an Angular 
 * application, ensuring a single instance of the service is available globally. 
 * It provides the ability to bind a virtual keyboard to an HTML input element, 
 * simulating various keypress actions. This is useful for scenarios where a 
 * touchscreen or custom input method is required.
 * 
 * @example
 * // Import the kioskService in a component
 * import { kioskService } from './kiosk.service';
 * 
 * constructor(private kioskService: kioskService) {}
 * 
 * // Show the keyboard with numeric input type
 * this.kioskService.show(inputElement, 'num');
 * 
 * // Hide the keyboard
 * this.kioskService.hide();
 */
@Injectable({ 
    providedIn: 'root' 
})
export class kioskService {
    private _OnKeyboardShow = new Subject<string>();

    /**
     * Emits when the keyboard is shown.
     * Subscribers receive the type of keyboard being shown (either 'password', 'num', 'price', or 'all').
     */    
    public OnKeyBoardShow = this._OnKeyboardShow.asObservable();

    private _OnKeyboardHide = new Subject<void>();

    /**
     * Emits when the keyboard is hidden.
     * Subscribers do not receive any data.
     */    
    public OnKeyBoardHide = this._OnKeyboardHide.asObservable();

    private _layouts: { [key: string]: string } = {
        'en': 'assets/kioskboard/kioskboard-keys-english.json',
        'es': 'assets/kioskboard/kioskboard-keys-spanish.json',
        'fr': 'assets/kioskboard/kioskboard-keys-french.json',
        'de': 'assets/kioskboard/kioskboard-keys-german.json',
        'hu': 'assets/kioskboard/kioskboard-keys-hungarian.json',
        'rs': 'assets/kioskboard/kioskboard-keys-russian.json',
        'tk': 'assets/kioskboard/kioskboard-keys-turkish.json'
    };

    private _layoutCache = new Map<string, KeyboardLayout>();
    private _loadPromises = new Map<string, Promise<KeyboardLayout>>();

    constructor(private http: HttpClient) {
        // nothing to do
    }

    /**
     * Loads a keyboard layout for the given language, using cache and request deduplication.
     * Falls back to the default language when no layout exists for the requested code.
     *
     * @param lg - The language code to load.
     */
    FetchLayout(lg: string): Promise<KeyboardLayout> {
        let _lg = lg.toLowerCase();

        if (!(_lg in this._layouts)){
            console.warn("[KIOSK] Layout not foung for language '" + _lg + "' (defaulting to '" + AppConstants.defaultLanguage + "')")
            _lg = AppConstants.defaultLanguage;
        }

        const _cached = this._layoutCache.get(_lg);
        if (_cached){
            return Promise.resolve(kioskService._CloneLayout(_cached));
        }

        let _pending = this._loadPromises.get(_lg);
        if (!_pending){
            _pending = new Promise<KeyboardLayout>((resolve, reject) => {
                this.http.get(this._layouts[_lg]).pipe(take(1)).subscribe({
                    next: (data) => {
                        const _layout = kioskService._CloneLayout(data as KeyboardLayout);
                        this._layoutCache.set(_lg, _layout);
                        this._loadPromises.delete(_lg);
                        resolve(_layout);
                    },
                    error: (error) => {
                        this._loadPromises.delete(_lg);
                        if (error.status != 0){
                            console.error("[HTTP] request error '" + error.status + "' in request to '" + this._layouts[_lg] + "'")
                        }
                        else {
                            console.error("[HTTP] status = 0");
                        }

                        reject(error);
                    },
                });
            });
            this._loadPromises.set(_lg, _pending);
        }

        return _pending.then((layout) => kioskService._CloneLayout(layout));
    }

    /**
     * Returns a shallow copy of a keyboard layout.
     * Cached layouts stay immutable: {@link UiKioskBoardComponent.keylayout} mutates
     * the last row in place (adds the `BR` key), and concurrent callers must not
     * share the same row objects held in {@link kioskService._layoutCache}.
     */
    private static _CloneLayout(layout: KeyboardLayout): KeyboardLayout {
        return layout.map((row) => ({ ...row }));
    }

    private _input: HTMLInputElement | HTMLTextAreaElement | null = null;

    /**
     * Shows the virtual keyboard and emits the type of keyboard.
     * 
     * @param input - The HTML input element where the keyboard will be displayed.
     * @param type - The type of keyboard to be displayed ('num', 'price', or 'all').
     */    
    show(input: HTMLInputElement | HTMLTextAreaElement, type: 'num' | 'price' | 'all'): void {
        this._input = input;
        if (!this._input.disabled){
            this._OnKeyboardShow.next((this._input.type == 'password') ? 'password' : type);
        }
    }
  
    /**
     * Hides the virtual keyboard and emits an event.
     */    
    hide(): void {
        this._OnKeyboardHide.next();
        this._input = null;
    }

    /**
     * Gets the current value of the input field associated with the keyboard.
     * 
     * @returns The current input value, or null if no input is set.
     */    
    get value() : string | null{
        if (this._input){
            return this._input.value;
        }
        return null;
    }

    /**
     * Simulates a key press on the virtual keyboard, modifying the input value accordingly.
     * 
     * @param key - The key to be pressed. Can be a character or special keys such as 'backspace' or 'breakline'.
     */    
    keyPress(key: string): void {
        if (this._input && !this._input.disabled) {
            const _value = this._input.value;

            let _inipos = this._input.selectionStart || 0;
            const _endpos = this._input.selectionEnd || 0;
    
            // dispatch the 'keydown' event
            const event = new KeyboardEvent('keydown', {
                key: key,
                bubbles: true,
                cancelable: true
            });

            this._input.dispatchEvent(event);
    
            // if event was not cancelled, we can continue
            if (!event.defaultPrevented) {
                switch (key) {
                    case 'backspace':
                        if (_inipos === _endpos) {
                            this._input.value = _value.substring(0, _inipos - 1) + _value.substring(_endpos);
                            _inipos -= 1;   // move the cursor backwards
                        } else {
                            this._input.value = _value.substring(0, _inipos) + _value.substring(_endpos);
                        }
                        break;
    
                    case 'breakline':
                        if (this._input.tagName.toLowerCase() === 'textarea') {
                            this._input.value = _value.substring(0, _inipos) + "\n" + _value.substring(_endpos);
                            _inipos += 1;   // move the cursor after the inserted character
                        }
                        break;
    
                    default:
                        this._input.value = _value.substring(0, _inipos) + key + _value.substring(_endpos);
                        _inipos += 1;   // move the cursor after the inserted character
                        break;
                }
                
                this._input.setSelectionRange(_inipos, _inipos);    // update the cursor position   
                this._input.dispatchEvent(new Event('input'));      // trigger for angular forms
                this._input.dispatchEvent(new Event('change'));     // trigger the change event
            }
        }
    }

    /**
     * Clears the value of the associated input field and triggers relevant events.
     */    
    ClearInput(): void {
        if (!this._input){
            return;
        }

        if (this._input && !this._input.disabled) {
            this._input.value = '';
        }

        this._input.dispatchEvent(new Event('input'));    // trigger for angular forms
        this._input.dispatchEvent(new Event('change'));   // trigger the change event        
    }  
}

/**********************************/
/* DIRECTIVE TO CONTROL KEYBOARD  */
/**********************************/

/**
 * @directive UiKioskInput
 * 
 * This directive controls the interaction between an input element and a virtual keyboard. 
 * When the input element gains focus, the directive triggers the virtual keyboard to display, 
 * and when the input loses focus, it hides the keyboard. The keyboard type is determined 
 * by a data attribute on the element.
 */
@Directive({
    selector: '[uppNgkiosk]'
})
export class UiKioskInputDirective {

    /**
     * Constructs the UiKioskInput directive.
     * @param el - A reference to the host DOM element where the directive is applied.
     * @param kiosk - The kiosk service that controls the virtual keyboard display.
     */    
    constructor(private el: ElementRef, private kiosk: kioskService){
        // nothing to do
    }

    /**
     * @event onFocus
     * 
     * Listens for the `focus` event on the host element. 
     * Displays the virtual keyboard when the element receives focus, unless the input is disabled.
     * 
     * @param input - The focused input element.
     */    
    @HostListener('focus', ['$event.target']) onFocus(input: HTMLInputElement) {
        if (!input.disabled){
            this.kiosk.show(input, this.el.nativeElement.getAttribute('data-kioskboard-type'));
        }
    }
    
    /**
     * @event onBlur
     * 
     * Listens for the `blur` event on the host element.
     * Hides the virtual keyboard when the element loses focus.
     */    
    @HostListener('blur') onBlur() {
        this.kiosk.hide();
    }
}

/**********************************/
/* KEYBOARD COMPONENT             */
/**********************************/

/**
 * The UiKioskBoard component represents a virtual keyboard for touch interfaces.
 * It manages multiple keyboard layouts, handles language changes, and processes 
 * keyboard interactions. The component supports alphanumeric and numeric layouts 
 * and can toggle between special character sets and regular character sets.
 */
@Component({
    selector: 'upp-kiosk-board',
    templateUrl:'./kiosk/ui-kiosk.html',
    styleUrls: [ 
        './kiosk/ui-kiosk.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiKioskBoardComponent implements OnInit, OnDestroy {
    private _type: string | null = null;

    @HostBinding('class.mobile')
    get IsMobile(): boolean {
        return this.view.Mobile === true;
    }

    /**
     * Gets the current keyboard type.
     */
    get type(): string | null {
        return this._type;
    }

    /**
     * Sets the keyboard type and triggers change detection.
     * @param value - The keyboard type ('num', 'price', or `null`).
     */    
    set type(value: string | null) {
        this._type = value;
        this.change.markForCheck();
    }

    private _capslock = false;

    /**
     * Gets the state of the Caps Lock key.
     */    
    get capslock(): boolean{
        return this._capslock;
    }

    /**
     * Sets the Caps Lock state and triggers change detection.
     * @param value - The new Caps Lock state.
     */    
    set capslock(value: boolean){
        if (this._capslock != value){
            this._capslock = value;
            this.change.markForCheck();
        }
    }

    private _specchar = false;

    /**
     * Gets the state of the special character toggle.
     */    
    get specchar(): boolean {
        return this._specchar;
    }

    /**
     * Sets the special character state and triggers change detection.
     * @param value - The new special character state.
     */    
    set specchar(value: boolean){
        if (this._specchar != value){
            this._specchar = value;
            this.change.markForCheck();
        }
    }

    /**
     * Gets the keyboard switch label based on the special character state.
     */    
    get switch(): string{
        if (this.specchar){
            return "abc";
        }
        else {
            return "*+=";
        }
    }

    private _spcLayout: KeyboardLayout = [
        { "0": "!", "1": "'", "2": "^", "3": "#", "4": "+", "5": "$", "6": "%", "7": "&", "8": "/", "9": "=" },
        { "0": "{", "1": "}", "2": "(", "3": ")", "4": "[", "5": "]", "6": "*", "7": "¿", "8": "?", "9": "|" },
        { "0": "@", "1": "-", "2": "_", "3": "€", "4": "~", "5": "<", "6": ">", "7": ",", "8": ";", "9": "ç" },
        { "0": "`", "1": "´", "2": ".", "3": ":", "4": "$", "5": "BR" }
    ]

    private _numLayout: KeyboardLayout  = [
        { "0": "7", "1": "8", "2": "9" },
        { "0": "4", "1": "5", "2": "6" },
        { "0": "1", "1": "2", "2": "3" },
        { "0": "0", "1": "RT", "2": "BK" }
    ]

    private _keyLayout: KeyboardLayout = [];    // loaded based on language

    /**
     * Gets the current keyboard layout based on the type and special character state.
     */    
    get keylayout(): KeyboardLayout  {
        if ((this.type == 'num') || (this.type == 'price')){
            return this._numLayout;
        }

        return (this.specchar) ? this._spcLayout : this._keyLayout;
    }

    /**
     * Sets a new keyboard layout and adds a break line key to the last row.
     * @param value - The new keyboard layout.
     */    


    set keylayout(value: KeyboardLayout) {
        if (value && value.length > 0) {
            const lastRow = value[value.length - 1];
            const lastKey = Math.max(...Object.keys(lastRow).map(Number));
            lastRow[(lastKey + 1).toString()] = 'BR';
        }
    
        this._keyLayout = value;
        this.change.markForCheck();
    }

    /**
     * Computes the width of each key based on the layout with the most keys.
     */    
    get keywidth(){
        let _maxkeys = 0;
        for (const _row of this._keyLayout){
            const _rowkeys = Object.keys(_row).length;
            if (_rowkeys > _maxkeys){
                _maxkeys = _rowkeys; 
            }
        }

        return "calc(" + (100/_maxkeys) + "% - 6px)";
    }

    /**
     * Formats the current input value based on the keyboard type.
     */    
    get inputValue(){
        if (this.kiosk.value){
            if (this._type == 'price'){
                return (Number(this.kiosk.value)/100).toFixed(2);
            }
            else {
                return this.kiosk.value.replace(/\n/g, '\u21B5');
            }
        }
        return '';
    }

    // do not loose focus on input when keyboard is pressed
    private _allowpropagation = false;

    /**
     * Prevents input focus loss when a key is pressed on the virtual keyboard.
     * @param event - The mouse event triggered by pressing a key.
     */    
    OnMouseDown(event: MouseEvent){
        if (this._allowpropagation == false){
            event.preventDefault();
            event.stopPropagation();    
        }

        this._allowpropagation = false;
    }

    /**
     * Allows input focus loss when the return key is pressed.
     */    
    OnPropagate(){
        this._allowpropagation = true;
    }

    /************************/
    /* MAIN STARTS HERE     */
    /************************/

    /**
     * Constructor for UiKioskBoard. Injects required services.
     * @param lang - The language service for handling language changes.
     * @param change - The change detector reference.
     * @param kiosk - The kiosk service for managing input and layout loading.
     */    
    constructor(
        private lang: languageService,
        private change: ChangeDetectorRef,
        private kiosk: kioskService,
        public view: viewService,
    ){
        // nothing to do
    }

    private _show_subscription: Subscription | null = null;
    private _hide_subscription: Subscription | null  = null;
    private _lang_subscription: Subscription | null  = null;

    private async _updateLayout(lg: string): Promise<void> {
        this.keylayout = await this.kiosk.FetchLayout(lg);
    }

    async ngOnInit() {
        await this._updateLayout(this.lang.GetLanguage(false));
        this._lang_subscription = this.lang.OnSetLanguage.subscribe(
        async () => {
            await this._updateLayout(this.lang.GetLanguage(false))
        });

        this._show_subscription = this.kiosk.OnKeyBoardShow.subscribe(
        type => {
            this.show(type);
        });

        this._hide_subscription = this.kiosk.OnKeyBoardHide.subscribe(
        () => {
            this.hide();
        });    
    }

    ngOnDestroy() {
        if (this._lang_subscription){
            this._lang_subscription.unsubscribe();
            this._lang_subscription = null;
        }

        if (this._show_subscription){
            this._show_subscription.unsubscribe();
            this._show_subscription = null;
        }

        if (this._hide_subscription){
            this._hide_subscription.unsubscribe();
            this._hide_subscription = null;
        }
    }

    /**
     * Displays the keyboard for the specified type.
     * @param type - The type of keyboard to display.
     */    
    show(type: string): void {
        this.type = type;
    }

   /**
     * Hides the virtual keyboard.
     */    
    hide(): void {
        this.type = null;
    }

    /**
     * Clears the current input value.
     */    
    clear(): void {
        this.kiosk.ClearInput()
    }

    public toL = false;
    public toR = false;

    /**
     * Toggles the keyboard layout to the left compact mode.
     */    
    lcompact(): void {
        this.toL = true;
        this.toR = false;
        this.change.markForCheck();
    }

    /**
     * Toggles the keyboard layout to the right compact mode.
     */    
    rcompact(): void {
        this.toL = false;
        this.toR = true;
        this.change.markForCheck();
    }

    /**
     * Expands the keyboard layout.
     */    
    expand(): void {
        this.toL = false;
        this.toR = false;
        this.change.markForCheck();
    }

    /**
     * Simulates a key press on the virtual keyboard.
     * @param key - The key to be pressed.
     */    
    onKeyPress(key: string): void {
        this.kiosk.keyPress(key);
        this.change.markForCheck();
    }

    /**
     * Toggles the Caps Lock state.
     */    
    capsLock(): void {
        this.capslock = !this.capslock;
        this.change.markForCheck();
    }  

    /**
     * Toggles the special character mode.
     */      
    specChar(): void {
        this.specchar = !this.specchar;
        this.change.markForCheck();
    }

    private _keyval(key: string){
        if (key.length > 1){   
            return key;     // special keys
        }

        return (this.capslock) ? key.toUpperCase() : key.toLowerCase();
    }

    /**
     * Retrieves the keys for a given row in the layout.
     * @param row - The keyboard layout row.
     * @returns An array of keys for the row.
     */    
    rowkeys(row: KeyboardLayoutRow) {
        const _keys = [];
        for (const _key of Object.keys(row)){
            _keys.push(this._keyval(row[_key]));
        }
        return _keys;
    }
}